import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, Target, Calendar, Users, Shield, CheckCircle, AlertCircle, TrendingUp, BookOpen } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

interface GouvernanceBZTelecomModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// DONNÉES — 6 PILIERS BZ TELECOM
// ============================================================================
const piliersBZTelecom = [
  {
    id: 1,
    nom: 'Gouvernance, Stratégie & Partenariats',
    avatars: ['Martin Carrier', 'Dany Bourassa', 'Benoit Dorval'],
    intention: 'Clarifier la vision BZ Telecom, structurer la gouvernance et choisir les bons partenariats pour soutenir la croissance 2026.',
    pourquoiCritique: 'Sans vision claire, gouvernance simple et bons partenaires, chaque pilier avance dans sa direction. Ce pilier aligne les décisions (où on va, avec qui, et à quel rythme).',
    concretementBZ: 'Vision 2026 BZ Telecom formalisée · priorités annuelles claires (téléphonie, câblage, innovation) · rencontres de gouvernance régulières · critères pour choisir/évaluer les partenaires (contracteurs, fournisseurs, technos) · décisions documentées et suivies dans le temps.',
    erreursFrequentes: [
      'Vision implicite (dans la tête de Martin) mais peu partagée',
      'Pas de lieux/formats clairs pour les décisions stratégiques',
      'Partenariats choisis opportunistiquement sans vrais critères',
      'Projets lancés qui ne sont pas alignés avec la stratégie 2026'
    ],
    niveaux: [
      { niveau: 0, label: 'Flou', description: 'Vision non écrite, décisions au cas par cas, partenariats choisis au feeling' },
      { niveau: 1, label: 'Intuitif', description: 'Vision discutée verbalement, quelques décisions structurées, partenariats existants mais peu évalués' },
      { niveau: 2, label: 'Structuré', description: 'Vision 2026 écrite, quelques priorités annuelles, rencontres de gouvernance de base' },
      { niveau: 3, label: 'Aligné', description: 'Vision et priorités connues de l’équipe, décisions revues régulièrement, premiers critères clairs pour les partenariats' },
      { niveau: 4, label: 'Piloté', description: 'Gouvernance rythmée (mensuelle/trimestrielle), décisions alignées avec les objectifs, portefeuille de partenariats piloté activement' },
      { niveau: 5, label: 'Référence', description: 'Gouvernance inspirante, vision claire, partenaires stratégiques solides, l’équipe comprend très bien où on s’en va et pourquoi' }
    ],
    aFaireMaintenant: [
      'Écrire une courte vision BZ Telecom 2026 (1 page max) et la partager à l’équipe',
      'Définir 3 à 5 priorités 2026 (revenus, produit, câblage, innovation)',
      'Mettre en place un rituel simple de gouvernance (ex. rencontre mensuelle BZ Telecom avec un ordre du jour fixe)',
      'Lister les partenaires actuels et futurs (contracteurs, fournisseurs, technos) avec quelques critères simples : qualité, fit avec la vision, potentiel de volume'
    ],
    preuvesVisibles: [
      'Vision BZ Telecom 2026 écrite et accessible',
      'Liste des priorités 2026 affichée (ou visible dans un document)',
      'Rituels de gouvernance planifiés dans les agendas (recurrents)',
      'Liste de partenaires avec une évaluation simple (qui garder, qui développer, qui ajouter)'
    ]
  },
  {
    id: 2,
    nom: 'Ventes, Marketing & Communication',
    avatars: ['Martin Carrier', 'Félix Bourassa'],
    intention: 'Générer des opportunités commerciales, augmenter la visibilité et convertir en revenus pour reprendre des parts de marché.',
    pourquoiCritique: 'Sans ventes structurées et visibilité, impossible d\'atteindre nos objectifs de croissance 2026. C\'est notre moteur de revenus et notre levier pour reprendre des parts de marché.',
    concretementBZ: 'Félix en charge ventes/dev affaires/marketing · processus de vente documenté · suivi pipeline · plan marketing structuré · présence réseaux sociaux · sites transactionnels · objectifs SMART mensuels · conversion mesurée · documentation claire des offres et campagnes.',
    erreursFrequentes: [
      'Pas de suivi des opportunités',
      'Processus de vente non documenté',
      'Communication marketing irrégulière',
      'Pas de plan marketing',
      'Objectifs flous ou non mesurés',
      'Pas de mesure de l\'impact marketing'
    ],
    niveaux: [
      { niveau: 0, label: 'Ad-hoc', description: 'Ventes au hasard, pas de processus, pas de marketing structuré' },
      { niveau: 1, label: 'Débutant', description: 'Quelques opportunités suivies, processus basique, posts ponctuels réseaux sociaux' },
      { niveau: 2, label: 'Structuré', description: 'Processus de vente documenté, pipeline suivi, plan marketing de base, publication régulière' },
      { niveau: 3, label: 'Performant', description: 'Pipeline optimisé, taux de conversion mesuré, plan marketing complet, sites transactionnels, contenu ciblé' },
      { niveau: 4, label: 'Excellence', description: 'Processus optimisé, conversion élevée, marketing automatisé, ROI mesuré, visibilité croissante' },
      { niveau: 5, label: 'Référence marché', description: 'Excellence reconnue, clients nous recommandent, BZ Telecom reconnu, parts de marché croissantes' }
    ],
    aFaireMaintenant: [
      'Documenter processus de vente (étapes claires)',
      'Mettre en place suivi pipeline (CRM ou outil simple)',
      'Créer plan marketing 2026 (objectifs, canaux, calendrier) et le documenter clairement',
      'Lancer présence régulière réseaux sociaux (2-3x/semaine)',
      'Développer sites transactionnels (priorité)',
      'Définir objectifs SMART mensuels (revenus, nombre clients, visibilité)',
      'Créer argumentaire de vente standard'
    ],
    preuvesVisibles: [
      'Pipeline à jour',
      'Taux de conversion mesuré',
      'Plan marketing documenté',
      'Sites transactionnels opérationnels',
      'Métriques visibilité',
      'Objectifs atteints',
      'Processus documenté'
    ]
  },
  {
    id: 3,
    nom: 'Produits Téléphonie & Innovation',
    avatars: ['Martin Carrier', 'Steven Vallée-Barette', 'Félix Bourassa'],
    intention: 'Maintenir et faire évoluer notre produit central (téléphonie Cloud Asterisk) et développer de nouveaux produits (agents AI vocaux) pour créer de nouvelles sources de revenus.',
    pourquoiCritique: 'La téléphonie Cloud Asterisk est notre cœur de métier et notre différenciateur. La baisse de 10% en 2025 doit être corrigée en 2026. L\'innovation est essentielle pour rester compétitifs et créer de nouvelles opportunités de revenus récurrents.',
    concretementBZ: 'Système téléphonique Cloud Asterisk · service client 24/7 · infrastructure redondante · évolution continue · développement agents AI vocaux · nouveaux produits téléphonie · innovation dans notre domaine vocal · qualité mesurée · revenus additionnels · documentation technique minimale mais à jour sur les changements.',
    erreursFrequentes: [
      'Évolution non planifiée',
      'Qualité non mesurée',
      'Service client réactif au lieu de proactif',
      'Pas de roadmap produit',
      'Innovation sans stratégie',
      'Produits non alignés avec notre expertise',
      'Pas de validation marché',
      'Innovation trop lente ou trop rapide'
    ],
    niveaux: [
      { niveau: 0, label: 'Instable/Statique', description: 'Système instable, service client réactif, aucune innovation, produits statiques' },
      { niveau: 1, label: 'Fonctionnel/Exploration', description: 'Système fonctionnel, service client basique, idées explorées, pas de développement' },
      { niveau: 2, label: 'Stable/Développement', description: 'Système stable, service client structuré, roadmap de base, produits en développement, validation marché' },
      { niveau: 3, label: 'Performant/Lancement', description: 'Système performant, service client proactif, évolution planifiée, nouveaux produits lancés, premiers revenus' },
      { niveau: 4, label: 'Excellence/Croissance', description: 'Système de référence, service client exemplaire, innovation continue, produits performants, revenus récurrents établis' },
      { niveau: 5, label: 'Leader marché/Référence', description: 'Référence marché, clients nous recommandent, différenciateur fort, innovation reconnue, produits différenciateurs, croissance forte' }
    ],
    aFaireMaintenant: [
      'Créer et tenir à jour une roadmap produit 2026 (fonctionnalités prioritaires téléphonie) avec critères qualité clairs',
      'Mettre en place métriques qualité (uptime, satisfaction client)',
      'Documenter processus service client',
      'Planifier améliorations infrastructure',
      'Valider opportunité agents AI vocaux (marché, compétences)',
      'Créer roadmap nouveaux produits 2026',
      'Définir critères succès nouveaux produits (revenus, adoption)',
      'Lancer développement premier produit'
    ],
    preuvesVisibles: [
      'Roadmap produit',
      'Roadmap innovation',
      'Métriques qualité',
      'Processus documenté',
      'Satisfaction client mesurée',
      'Produits en développement',
      'Premiers revenus nouveaux produits',
      'Adoption mesurée'
    ]
  },
  {
    id: 4,
    nom: 'Division Câblage',
    avatars: ['Martin Carrier', 'Benoit Dorval', 'Simon Cantin'],
    intention: 'Structurer et faire croître la division câblage tout en maintenant une qualité exemplaire sur les chantiers.',
    pourquoiCritique: 'Le câblage est sur une lancée et a fortement aidé 2025. En 2026, c’est un levier clé pour augmenter les revenus sans dépendre uniquement de la téléphonie.',
    concretementBZ: 'Projets câblage planifiés · équipe de câbleurs structurée · standards clairs par type d’intervention · contrôle qualité systématique · respect des délais et des devis · amélioration continue basée sur les retours de chantier.',
    erreursFrequentes: [
      'Qualité sacrifiée pour gagner du volume',
      'Mauvaise planification des chantiers (sur ou sous-charge)',
      'Délais non respectés',
      'Absence de contrôle qualité en fin de chantier',
      'Communication incomplète entre vente, estimation et câblage'
    ],
    niveaux: [
      { niveau: 0, label: 'Chaotique', description: 'Projets non structurés, qualité variable, planning inexistant' },
      { niveau: 1, label: 'Basique', description: 'Quelques projets bien livrés, mais peu de standards, dépend beaucoup des individus' },
      { niveau: 2, label: 'Structuré', description: 'Processus câblage documenté, checklists par type de mandat, suivi simple des délais' },
      { niveau: 3, label: 'Performant', description: 'Planning maîtrisé, volume en croissance, qualité stable, peu de reprises' },
      { niveau: 4, label: 'Excellence', description: 'Câblage très bien huilé, productivité élevée, qualité exemplaire, clients très satisfaits' },
      { niveau: 5, label: 'Référence', description: 'Division câblage vue comme un modèle, forte réputation, clients et contracteurs nous recommandent' }
    ],
    aFaireMaintenant: [
      'Documenter le processus câblage (de la demande jusqu’à la fin de chantier)',
      'Définir des standards qualité par type d’intervention (petits travaux, projets majeurs, etc.)',
      'Mettre en place un planning visuel des chantiers (hebdo/mensuel) pour l’équipe câblage',
      'Suivre quelques indicateurs simples : délais, reprises, satisfaction client après chantier et en faire une revue mensuelle pour s’améliorer'
    ],
    preuvesVisibles: [
      'Processus câblage documenté et accessible',
      'Planning des chantiers visible et à jour',
      'Taux de reprises faible',
      'Retours clients positifs sur les travaux de câblage'
    ]
  },
  {
    id: 5,
    nom: 'Opérations & Service Client',
    avatars: ['Martin Carrier', 'Steven Vallée-Barette'],
    intention: 'Livrer un service client exemplaire 24/7 pour maintenir la satisfaction et réduire le churn.',
    pourquoiCritique: 'Le service client est notre différenciateur "service humain inclus". C\'est ce qui nous distingue de la concurrence.',
    concretementBZ: 'Service client 24/7 · réponse rapide · résolution efficace · satisfaction mesurée · churn réduit · procédures support documentées et tenues à jour.',
    erreursFrequentes: [
      'Réponse lente',
      'Problèmes non résolus',
      'Satisfaction non mesurée',
      'Service réactif au lieu de proactif'
    ],
    niveaux: [
      { niveau: 0, label: 'Réactif', description: 'Service réactif, réponse lente, problèmes non anticipés' },
      { niveau: 1, label: 'Basique', description: 'Service basique, réponse dans délais raisonnables' },
      { niveau: 2, label: 'Structuré', description: 'Service structuré, processus documenté, satisfaction mesurée' },
      { niveau: 3, label: 'Performant', description: 'Service performant, réponse rapide, satisfaction élevée' },
      { niveau: 4, label: 'Excellence', description: 'Service exemplaire, proactif, satisfaction très élevée' },
      { niveau: 5, label: 'Référence', description: 'Référence marché, clients nous recommandent, churn minimal' }
    ],
    aFaireMaintenant: [
      'Documenter processus service client (réponse, résolution)',
      'Mettre en place métriques satisfaction (NPS, temps réponse)',
      'Créer et maintenir une base connaissances (FAQ, solutions communes) comme référence qualité',
      'Définir objectifs service (temps réponse, satisfaction)'
    ],
    preuvesVisibles: [
      'Processus documenté',
      'Métriques satisfaction',
      'Base connaissances',
      'Objectifs atteints'
    ]
  },
  {
    id: 6,
    nom: 'Administration, Infrastructure & Matériel',
    avatars: ['Martin Carrier', 'Benoit Dorval', 'Émilie Quintal'],
    intention: 'Gérer efficacement les finances, l\'administration, maintenir une infrastructure cloud performante, et sélectionner/approvisionner le bon matériel pour soutenir la croissance.',
    pourquoiCritique: 'Une administration solide est la base de toute croissance. L\'infrastructure est la base de tous nos services. Le mauvais choix de matériel ou les délais d\'approvisionnement peuvent faire échouer des projets.',
    concretementBZ: 'Facturation efficace · finances suivies · contrats gérés · infrastructure cloud redondante · performance optimisée · sécurité maintenue · sélection matériel optimisée · approvisionnement efficace · stocks gérés · coûts contrôlés · croissance soutenue · documentation fiable des processus critiques.',
    erreursFrequentes: [
      'Facturation tardive',
      'Finances non suivies',
      'Contrats non centralisés',
      'Administration chaotique',
      'Infrastructure non redondante',
      'Performance non optimisée',
      'Sécurité négligée',
      'Monitoring insuffisant',
      'Mauvais choix matériel',
      'Délais approvisionnement trop longs',
      'Stocks non gérés',
      'Coûts non contrôlés'
    ],
    niveaux: [
      { niveau: 0, label: 'Chaotique/Instable/Ad-hoc', description: 'Administration chaotique, infrastructure instable, choix matériel au hasard, approvisionnement chaotique' },
      { niveau: 1, label: 'Basique', description: 'Administration basique, facturation manuelle, infrastructure basique, redondance partielle, choix basique, approvisionnement manuel' },
      { niveau: 2, label: 'Structuré', description: 'Processus documenté, facturation régulière, finances suivies, infrastructure stable, redondance de base, sélection basée sur critères, stocks suivis' },
      { niveau: 3, label: 'Performant', description: 'Administration efficace, facturation automatisée, infrastructure performante, redondance complète, sélection optimisée, approvisionnement efficace, coûts contrôlés' },
      { niveau: 4, label: 'Excellence', description: 'Administration exemplaire, processus optimisés, infrastructure exemplaire, performance optimisée, sélection exemplaire, approvisionnement optimisé, stocks optimaux' },
      { niveau: 5, label: 'Référence', description: 'Référence marché, administration modèle, infrastructure modèle, uptime exemplaire, processus modèle, avantage concurrentiel' }
    ],
    aFaireMaintenant: [
      'Documenter processus facturation (délais, suivi)',
      'Mettre en place suivi finances (revenus, dépenses, cashflow)',
      'Centraliser gestion contrats (base données)',
      'Auditer infrastructure actuelle (redondance, performance)',
      'Mettre en place monitoring complet (uptime, performance)',
      'Documenter architecture infrastructure',
      'Créer catalogue matériel approuvé (critères qualité/prix)',
      'Documenter processus approvisionnement (fournisseurs, délais)',
      'Mettre en place suivi stocks (niveaux, coûts)',
      'Définir objectifs administration/infrastructure/approvisionnement (SMART)'
    ],
    preuvesVisibles: [
      'Processus documenté',
      'Finances suivies',
      'Contrats centralisés',
      'Architecture documentée',
      'Monitoring actif',
      'Uptime mesuré',
      'Catalogue matériel',
      'Stocks suivis',
      'Objectifs atteints'
    ]
  }
];

// ============================================================================
// DONNÉES — MATURITÉ ACTUELLE ET CIBLE
// ============================================================================
const maturiteData = [
  { axe: 'Vision & Stratégie', actuel: 2, cible: 4 },
  { axe: 'Gouvernance & rôles', actuel: 2, cible: 4 },
  { axe: 'PRIsME Client', actuel: 3, cible: 5 },
  { axe: 'PRIsME Employé et Département', actuel: 1, cible: 4 },
  { axe: 'Innovation & Évolution', actuel: 2, cible: 4 },
  { axe: 'Livraison & Exécution', actuel: 3, cible: 5 },
  { axe: 'Communication & Commercialisation', actuel: 1, cible: 3 },
  { axe: 'Qualité, KPI & Suivi', actuel: 1, cible: 3 },
  { axe: 'Formation & Adoption', actuel: 2, cible: 4 },
  { axe: 'Technologie & Outils', actuel: 3, cible: 5 }
];

// ============================================================================
// DONNÉES — RACI (BZ TELECOM)
// ============================================================================
const raciData = [
  {
    activite: 'Ventes, marketing & développement d’affaires',
    roles: {
      'Martin Carrier': 'A',
      'Félix Bourassa': 'R',
      'Dany Bourassa': 'C',
      'Steven Vallée-Barette': 'I',
      'Benoit Dorval': 'I',
      'Simon Cantin': 'I',
      'Émilie Quintal': 'I'
    }
  },
  {
    activite: 'Téléphonie Cloud & innovation produit',
    roles: {
      'Martin Carrier': 'A',
      'Steven Vallée-Barette': 'R',
      'Félix Bourassa': 'C',
      'Dany Bourassa': 'C',
      'Benoit Dorval': 'I',
      'Simon Cantin': 'I',
      'Émilie Quintal': 'I'
    }
  },
  {
    activite: 'Division câblage (projets & chantiers)',
    roles: {
      'Martin Carrier': 'A',
      'Benoit Dorval': 'R',
      'Simon Cantin': 'R',
      'Dany Bourassa': 'C',
      'Émilie Quintal': 'C',
      'Steven Vallée-Barette': 'I',
      'Félix Bourassa': 'I'
    }
  },
  {
    activite: 'Opérations & service client',
    roles: {
      'Martin Carrier': 'A',
      'Steven Vallée-Barette': 'R',
      'Émilie Quintal': 'C',
      'Dany Bourassa': 'C',
      'Benoit Dorval': 'I',
      'Simon Cantin': 'I',
      'Félix Bourassa': 'I'
    }
  },
  {
    activite: 'Administration, infrastructure & matériel',
    roles: {
      'Martin Carrier': 'A',
      'Émilie Quintal': 'R',
      'Steven Vallée-Barette': 'R',
      'Benoit Dorval': 'R',
      'Dany Bourassa': 'C',
      'Simon Cantin': 'I',
      'Félix Bourassa': 'I'
    }
  },
  {
    activite: 'Qualité & assurance globale',
    roles: {
      'Martin Carrier': 'A',
      'Benoit Dorval': 'R',
      'Steven Vallée-Barette': 'R',
      'Simon Cantin': 'R',
      'Félix Bourassa': 'C',
      'Émilie Quintal': 'C',
      'Dany Bourassa': 'C'
    }
  },
  {
    activite: 'Gouvernance BZ Telecom & comité mensuel',
    roles: {
      'Martin Carrier': 'A',
      'Dany Bourassa': 'R',
      'Félix Bourassa': 'C',
      'Benoit Dorval': 'I',
      'Steven Vallée-Barette': 'I',
      'Simon Cantin': 'I',
      'Émilie Quintal': 'I'
    }
  },
  {
    activite: 'Partenariats stratégiques & BOT AI',
    roles: {
      'Martin Carrier': 'A',
      'Félix Bourassa': 'R',
      'Steven Vallée-Barette': 'R',
      'Dany Bourassa': 'C',
      'Benoit Dorval': 'I',
      'Simon Cantin': 'I',
      'Émilie Quintal': 'I'
    }
  }
];

const avatarMap: Record<string, string> = {
  'Dany Bourassa': '/DB.png',
  'Éric Hains': '/EH.png',
  'Francis Marquis': '/FM.png',
  'Jean-Michel Caouette': '/JMC.png',
  'Jean-Pierre Leclerc': '/JP.png',
  'Pierre-Luc Fortin': '/PLF.png',
  'Martin Carrier': '/MC.png',
  'Benoit Dorval': '/BD.png',
  'Steven Vallée-Barette': '/SVB.png',
  'Simon Cantin': '/SC.png',
  'Félix Bourassa': '/FB.png',
  'Émilie Quintal': '/EQ.png',
  'Tous': '👥', // Pour d'éventuels usages génériques
  'Gestionnaire': '👤', // Special case - will render as emoji
  'Comité PRIsME': '/LogoPrismeSmall.png',
  'Primes': '/BZ.png',
  'Directeurs de compte': '$' // Special case - will render as symbol
};

// ============================================================================
// DONNÉES — ROADMAP 2026
// ============================================================================
const roadmap2026 = [
  {
    trimestre: 'Q1 2026',
    intention: 'Fondations opérationnelles — Déploiement PRIsME Employé et intégration gestion de projets',
    axes: [
      {
        axe: 'PRIsME Employé et Département',
        intention: 'Déployer PRIsME Employé pour tous les rôles et responsabiliser les gestionnaires sur leur contenu départemental',
        livrables: [
          { texte: 'Déploiement PRIsME Employé pour 50% des employés', effort: 40, priorite: 'Haute' },
          { texte: 'Onboarding intégré à PRIsME Employé avec checklist par rôle', effort: 20, priorite: 'Haute' },
          { texte: 'Documentation processus par rôle (responsabilités, livrables, outils)', effort: 30, priorite: 'Haute' },
          { texte: 'Formation gestionnaires sur leur responsabilité contenu départemental', effort: 15, priorite: 'Moyenne' },
          { texte: 'Pages PRIsME Employé versionnées et mises à jour régulièrement', effort: 10, priorite: 'Moyenne' },
          { texte: 'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)', effort: 15, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Livraison & Exécution',
        intention: 'Augmenter livraisons PRIsME Client (15→50) et cloner TactiCE (Projet) dans PRIsME',
        livrables: [
          { texte: 'Augmenter livraisons PRIsME Client de 15 à 50 livrables', effort: 40, priorite: 'Haute' },
          { texte: 'Cloner TactiCE (Projet) dans PRIsME avec structure complète', effort: 50, priorite: 'Haute' },
          { texte: 'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH', effort: 25, priorite: 'Haute' },
          { texte: 'Intégration projets TactiCE avec PRIsME Client (livraison traçable)', effort: 30, priorite: 'Haute' },
          { texte: 'Visibilité risques/avancement/valeur livrée par projet', effort: 20, priorite: 'Moyenne' },
          { texte: 'Templates projets PRIsME (kickoff, suivi, rétrospective)', effort: 15, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Technologie & Outils (BOT)',
        intention: 'Rendre le BOT PRIsME opérationnel pour automatiser les tâches répétitives',
        livrables: [
          { texte: 'BOT PRIsME opérationnel (réponses FAQ, guidage processus)', effort: 50, priorite: 'Haute' },
          { texte: 'Automatisation tâches répétitives (création pages, notifications, rappels)', effort: 40, priorite: 'Haute' },
          { texte: 'Intégration workflows PRIsME (escalade, approbations, suivi)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Interface conversationnelle pour guider les Primes', effort: 25, priorite: 'Moyenne' },
          { texte: 'Analytics utilisation BOT (questions fréquentes, points de friction)', effort: 15, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Innovation & Évolution',
        intention: 'Créer le catalogue d\'actifs et améliorer la gestion documentaire',
        livrables: [
          { texte: 'Catalogue d\'actifs TI complet (inventaire, classification, tagging)', effort: 40, priorite: 'Moyenne' },
          { texte: 'Interface de recherche avancée catalogue (filtres, tags, relations)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)', effort: 25, priorite: 'Haute' },
          { texte: 'Automatisation génération documents (rapports, livrables)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Sécurité intégrée (classification, accès, audit)', effort: 20, priorite: 'Haute' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q2 2026',
    intention: 'Structuration gouvernance et mesure — Formaliser les processus et mesurer l\'impact',
    axes: [
      {
        axe: 'Gouvernance & Décisions',
        intention: 'Formaliser la gouvernance PRIsME avec processus décision clairs et lien avec Shield',
        livrables: [
          { texte: 'RACI complet documenté et visible dans PRIsME', effort: 10, priorite: 'Haute' },
          { texte: 'Processus décision formalisé (critères, escalade, délais)', effort: 20, priorite: 'Haute' },
          { texte: 'Lien avec Shield / Decision Desk opérationnel (firewall mental)', effort: 15, priorite: 'Moyenne' },
          { texte: 'Format Decision Packet standardisé pour escalade vers Dany', effort: 10, priorite: 'Moyenne' },
          { texte: 'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)', effort: 8, priorite: 'Basse' },
          { texte: 'Règles d\'escalade claires (quand escalader, quand non)', effort: 12, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Qualité, KPI & Suivi',
        intention: 'Mettre en place les tableaux de bord KPI et métriques qualité automatisées',
        livrables: [
          { texte: 'Tableaux de bord KPI opérationnels (adoption, qualité, valeur livrée)', effort: 40, priorite: 'Haute' },
          { texte: 'Métriques qualité automatisées (conformité PRIsME, satisfaction client)', effort: 30, priorite: 'Haute' },
          { texte: 'Rapports réguliers au Comité PRIsME (mensuel, trimestriel)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Dashboard temps réel maturité PRIsME (spider chart dynamique)', effort: 35, priorite: 'Moyenne' },
          { texte: 'KPI PRIsME Employé (adoption, autonomie, temps onboarding)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Métriques projets (livraison dans délais, budget, qualité)', effort: 30, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Formation & Adoption',
        intention: 'Structurer le programme de formation et identifier les champions PRIsME',
        livrables: [
          { texte: 'Programme formation structuré (onboarding, avancé, spécialisé)', effort: 50, priorite: 'Haute' },
          { texte: 'Champions PRIsME identifiés et formés (1 par département)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Mesure adoption mise en place (taux utilisation, compétences)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Parcours formation par rôle (Prime, Chef projet, Gestionnaire)', effort: 40, priorite: 'Haute' },
          { texte: 'Certification PRIsME interne (niveaux 1, 2, 3)', effort: 30, priorite: 'Basse' },
          { texte: 'Bibliothèque ressources formation (vidéos, guides, templates)', effort: 35, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'PRIsME Employé et Département',
        intention: 'Atteindre adoption universelle et autonomie gestionnaires',
        livrables: [
          { texte: 'Tous les rôles ont leur PRIsME Employé complet et à jour', effort: 30, priorite: 'Haute' },
          { texte: 'Gestionnaires autonomes sur leur contenu départemental', effort: 20, priorite: 'Haute' },
          { texte: 'Mise à jour régulière PRIsME Employé (mensuelle minimum)', effort: 15, priorite: 'Moyenne' },
          { texte: 'Feedback boucle intégrée (suggestions, améliorations)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)', effort: 25, priorite: 'Basse' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q3 2026',
    intention: 'Communication et commercialisation — Positionner PRIsME comme différenciateur stratégique',
    axes: [
      {
        axe: 'Communication Interne',
        intention: 'Améliorer la communication interne PRIsME et l\'engagement des équipes',
        livrables: [
          { texte: 'Stratégie communication interne PRIsME (fréquence, canaux, messages)', effort: 15, priorite: 'Moyenne' },
          { texte: 'Newsletter PRIsME mensuelle (succès, nouveautés, formations)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Champions PRIsME actifs dans communication (relais, témoignages)', effort: 15, priorite: 'Basse' },
          { texte: 'Intranet PRIsME enrichi (actualités, ressources, FAQ)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Sessions partage d\'expérience (retours terrain, bonnes pratiques)', effort: 20, priorite: 'Basse' },
          { texte: 'Métriques engagement communication (ouverture, clics, participation)', effort: 15, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Commercialisation PRIsME',
        intention: 'Développer les arguments de vente PRIsME et documenter les cas clients',
        livrables: [
          { texte: 'Arguments de vente PRIsME standardisés (value proposition, différenciateurs)', effort: 25, priorite: 'Haute' },
          { texte: 'Cas clients documentés (avant/après, ROI, témoignages)', effort: 40, priorite: 'Haute' },
          { texte: 'Formation équipes commerciales sur PRIsME (pitch, objections)', effort: 30, priorite: 'Haute' },
          { texte: 'Matériel commercial PRIsME (présentations, one-pagers, démos)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Stratégie PULL commercialisation (contenu, webinaires, événements)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Suivi opportunités PRIsME (pipeline, conversion, valeur)', effort: 20, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Livraison PRIsME Client',
        intention: 'Optimiser le processus de livraison PRIsME Client et garantir qualité 100%',
        livrables: [
          { texte: 'Processus livraison PRIsME Client optimisé (workflow, checkpoints)', effort: 30, priorite: 'Haute' },
          { texte: 'Qualité 100% vérifiée avant livraison (checklist, validation)', effort: 25, priorite: 'Haute' },
          { texte: 'Templates livraison PRIsME Client standardisés (structure, contenu)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Formation équipes livraison (processus, qualité, client)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Métriques livraison (temps, qualité, satisfaction client)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Amélioration continue processus (rétrospectives, ajustements)', effort: 15, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Innovation & Évolution',
        intention: 'Préparer l\'évolution PRIsME et intégrer nouvelles fonctionnalités',
        livrables: [
          { texte: 'Roadmap 2027 préparée (priorités, intentions, livrables)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Expérimentations nouvelles fonctionnalités (POC, validations)', effort: 40, priorite: 'Basse' },
          { texte: 'Intégration ARIA dans PRIsME (si pertinent)', effort: 50, priorite: 'Basse' },
          { texte: 'Amélioration continue basée sur feedback (équipes, clients)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Veille technologique PRIsME (outils, méthodes, tendances)', effort: 20, priorite: 'Basse' },
          { texte: 'Évolution architecture PRIsME (scalabilité, performance)', effort: 45, priorite: 'Moyenne' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q4 2026',
    intention: 'Maturité et vision 2027 — Atteindre niveau 4 et préparer l\'avenir',
    axes: [
      {
        axe: 'Vision & Stratégie PRIsME',
        intention: 'Définir la vision 2027 et aligner stratégie PRIsME avec objectifs BZ',
        livrables: [
          { texte: 'Vision 2027 PRIsME définie (objectifs, axes, maturité cible)', effort: 40, priorite: 'Haute' },
          { texte: 'Alignement stratégique complet (PRIsME ↔ objectifs BZ)', effort: 25, priorite: 'Haute' },
          { texte: 'PRIsME comme différenciateur reconnu (interne et externe)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Positionnement marché PRIsME (concurrents, avantages, opportunités)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Stratégie croissance PRIsME (scalabilité, nouveaux marchés)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Documentation vision stratégique (présentation, plan, communication)', effort: 25, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Technologie & Outils (BOT)',
        intention: 'Optimiser la stack technologique et intégrations avancées',
        livrables: [
          { texte: 'Stack optimisée et performante (performance, scalabilité)', effort: 50, priorite: 'Moyenne' },
          { texte: 'Intégrations avancées (outils quotidiens, systèmes clients)', effort: 60, priorite: 'Moyenne' },
          { texte: 'Performance exemplaire (temps réponse, disponibilité)', effort: 40, priorite: 'Haute' },
          { texte: 'BOT PRIsME avancé (apprentissage, personnalisation)', effort: 55, priorite: 'Basse' },
          { texte: 'Outils Charge de projet IA intégré (estimation, planification)', effort: 70, priorite: 'Moyenne' },
          { texte: 'Monitoring et alertes proactives (santé système, anomalies)', effort: 35, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Maturité globale',
        intention: 'Atteindre niveau 4 sur axes critiques et démontrer ROI mesurable',
        livrables: [
          { texte: 'Niveau 4 atteint sur axes critiques (PRIsME Client, Livraison, Technologie)', effort: 0, priorite: 'Haute' },
          { texte: 'ROI mesurable et documenté (temps économisé, qualité améliorée, valeur)', effort: 40, priorite: 'Haute' },
          { texte: 'PRIsME référence marché (cas clients, témoignages, reconnaissance)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Audit maturité PRIsME complet (10 axes, gaps, plan amélioration)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Rapport annuel PRIsME 2026 (succès, métriques, vision 2027)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Célébration succès PRIsME (équipe, clients, partenaires)', effort: 10, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Gestion documentaire PRIsME',
        intention: 'Finaliser la gestion documentaire impeccable et automatisation',
        livrables: [
          { texte: 'Export PDF 8 1/2 x 11 impeccable pour tous documents PRIsME', effort: 30, priorite: 'Haute' },
          { texte: 'Automatisation complète génération documents (rapports, livrables)', effort: 45, priorite: 'Moyenne' },
          { texte: 'Gestion version documents (historique, comparaison, rollback)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Templates documents standardisés (PRIsME Client, Employé, Projets)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Archivage intelligent documents (rétention, classification)', effort: 30, priorite: 'Basse' },
          { texte: 'Recherche documentaire avancée (full-text, tags, métadonnées)', effort: 40, priorite: 'Basse' }
        ]
      }
    ]
  }
];

export default function GouvernanceBZTelecomModal({ isOpen, onClose }: GouvernanceBZTelecomModalProps) {
  const [activeTab, setActiveTab] = useState<'esprit' | 'cadre' | 'maturite' | 'raci' | 'roadmap' | 'rituels' | 'reunions'>('esprit');

  const tabs = [
    { id: 'esprit' as const, label: 'Esprit & Objectifs macro', icon: Eye },
    { id: 'cadre' as const, label: 'Cadre holistique (coaching)', icon: BookOpen },
    { id: 'maturite' as const, label: 'Maturité (Spider + Bar)', icon: Target },
    { id: 'raci' as const, label: 'Rôles & responsabilités', icon: Users },
    { id: 'reunions' as const, label: 'Réunions', icon: Calendar },
    { id: 'roadmap' as const, label: 'Roadmap 2026', icon: Calendar },
    { id: 'rituels' as const, label: 'Gouvernance & rituels', icon: Shield },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-indigo-500/30 shadow-2xl shadow-indigo-500/20 flex flex-col">
              {/* Header */}
              <div className="relative p-3 border-b border-indigo-500/30 bg-gradient-to-r from-orange-900/40 via-slate-900/60 to-orange-900/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-300 to-orange-500">
                        Gouvernance BZ Telecom
                      </h2>
                      <p className="text-gray-400 text-xs">
                        Cadre holistique de gouvernance, maturité et rôles pour l’équipe BZ Telecom
                      </p>
                    </div>
                    {/* Avatars participants clés */}
                    <div className="flex items-center gap-2">
                      <img src="/MC.png" alt="Martin Carrier" className="w-8 h-8 rounded-full border-2 border-orange-400" title="Martin Carrier - Président BZ Telecom" />
                      <img src="/BD.png" alt="Benoit Dorval" className="w-8 h-8 rounded-full border-2 border-orange-400" title="Benoit Dorval - Directeur câblage" />
                      <img src="/SVB.png" alt="Steven Vallée-Barette" className="w-8 h-8 rounded-full border-2 border-orange-400" title="Steven Vallée-Barette - Téléphonie & innovation" />
                      <img src="/SC.png" alt="Simon Cantin" className="w-8 h-8 rounded-full border-2 border-orange-400" title="Simon Cantin - Chef d’équipe câblage" />
                      <img src="/FB.png" alt="Félix Bourassa" className="w-8 h-8 rounded-full border-2 border-orange-400" title="Félix Bourassa - Ventes & développement des affaires" />
                      <img src="/DB.png" alt="Dany Bourassa" className="w-8 h-8 rounded-full border-2 border-orange-400" title="Dany Bourassa - Coach gouvernance externe" />
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors ml-2"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-indigo-500/30 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-orange-600 to-orange-700 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <AnimatePresence mode="wait">
                  {activeTab === 'esprit' && <EspritTab />}
                  {activeTab === 'cadre' && <CadreTab />}
                  {activeTab === 'maturite' && <MaturiteTab />}
                  {activeTab === 'raci' && <RACITab />}
                  {activeTab === 'reunions' && <ReunionsTab />}
                  {activeTab === 'roadmap' && <RoadmapTab />}
                  {activeTab === 'rituels' && <RituelsTab />}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// ONGLET 1 — ESPRIT & OBJECTIFS MACRO
// ============================================================================
function EspritTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-orange-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Esprit & Objectifs macro
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Vision BZ Telecom 2026 — pourquoi cette gouvernance existe, ce qu'elle transforme, comment elle guide notre croissance
        </p>
      </div>

      {/* Vision BZ Telecom 2026 */}
      <div className="bg-white/5 rounded-xl p-6 border border-orange-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-orange-400" />
          Vision BZ Telecom 2026
        </h4>
        <p className="text-gray-300 leading-relaxed mb-3 text-lg font-semibold">
          Cette modal de gouvernance est le point central de 2026.
        </p>
        <p className="text-gray-300 leading-relaxed mb-3">
          Elle est notre <strong className="text-white">tableau de bord stratégique mensuel</strong> pour suivre l'évolution de BZ Telecom tout au long de l'année. Chaque mois, nous y consultons nos <strong className="text-white">revenus et nos parts de marché</strong> — les deux enjeux prioritaires qui guident toutes nos décisions.
        </p>
        <p className="text-gray-300 leading-relaxed mb-3">
          Cette gouvernance existe pour que <strong className="text-white">chacun de nos 5 membres d'équipe comprenne les enjeux télécom et la vision</strong>. Elle structure notre façon de travailler ensemble, de prendre des décisions et de mesurer notre succès.
        </p>
        <p className="text-gray-300 leading-relaxed">
          BZ Telecom n'est pas juste un fournisseur de téléphonie cloud. Nous sommes des <strong className="text-white">experts en téléphonie Cloud Asterisk avec service humain inclus</strong> — c'est cette différence qui fait notre excellence et notre valeur.
        </p>
      </div>

      {/* Pourquoi cette gouvernance existe */}
      <div className="bg-white/5 rounded-xl p-6 border border-orange-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-orange-400" />
          Pourquoi cette gouvernance existe
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Cette gouvernance existe pour répondre à un enjeu fondamental :<br />
          <strong className="text-white">aligner toute l'équipe sur les mêmes objectifs, comprendre les enjeux télécom, et mesurer notre progression vers la croissance</strong>.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Concrètement, cette gouvernance permet de :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Suivre nos revenus et parts de marché mensuellement</strong>
              <p className="text-sm text-gray-400 mt-1">Ces deux métriques sont nos enjeux #1. Chaque mois, nous les consultons ici pour prendre des décisions éclairées.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Comprendre les enjeux télécom en équipe</strong>
              <p className="text-sm text-gray-400 mt-1">Chacun des 5 membres (Martin, Benoit, Steven, Simon, Félix) comprend pourquoi nous faisons ce que nous faisons et comment son rôle contribue à la vision.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Structurer nos décisions et nos rôles</strong>
              <p className="text-sm text-gray-400 mt-1">La matrice RACI (Responsable, Accountable, Consulted, Informed) clarifie qui fait quoi, qui décide, et qui doit être consulté ou informé.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Apprendre le jargon télécom progressivement</strong>
              <p className="text-sm text-gray-400 mt-1">Téléphonie Cloud Asterisk, parts de marché, MRR (Monthly Recurring Revenue), agents AI vocaux — nous expliquons ces concepts pour que tout le monde comprenne.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Mesurer notre progression vers les objectifs 2026</strong>
              <p className="text-sm text-gray-400 mt-1">Nous suivons où nous en sommes, ce qui fonctionne, et ce qui doit être ajusté pour atteindre nos objectifs de croissance.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* État actuel 2025 et enjeux */}
      <div className="bg-white/5 rounded-xl p-6 border border-orange-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-orange-400" />
          État actuel 2025 et enjeux
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Pour comprendre où nous allons en 2026, il faut d'abord comprendre où nous sommes aujourd'hui :
        </p>
        <ul className="space-y-4 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Câblage : sur une lancée, a aidé 2025 à conserver ses acquis</strong>
              <p className="text-sm text-gray-400 mt-1">Notre division câblage performe bien. Benoit Dorval (directeur câblage) et Simon Cantin (chef équipe câbleur) maintiennent la qualité et la croissance. Nous avons des partenariats contracteurs qui fonctionnent bien.</p>
              <p className="text-sm text-emerald-400 mt-1">→ En 2026 : augmenter le volume en développant de nouveaux partenariats contracteurs.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Téléphonie cloud : légère baisse de 10% en 2025</strong>
              <p className="text-sm text-gray-400 mt-1">Notre cœur de métier — la téléphonie Cloud Asterisk — a connu une baisse de revenus. C'est notre enjeu principal à résoudre en 2026.</p>
              <p className="text-sm text-orange-400 mt-1">→ En 2026 : reprendre des parts de marché en téléphonie cloud grâce à l'innovation marketing, ventes et produits.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Manque de visibilité sur les revenus et parts de marché</strong>
              <p className="text-sm text-gray-400 mt-1">Sans suivi mensuel structuré, difficile de savoir où nous en sommes réellement et de prendre des décisions rapides.</p>
              <p className="text-sm text-emerald-400 mt-1">→ Cette gouvernance devient notre tableau de bord mensuel pour suivre ces métriques critiques.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Besoin de structuration pour l'équipe</strong>
              <p className="text-sm text-gray-400 mt-1">Avec 5 membres d'équipe, chacun doit comprendre les enjeux télécom, la vision, et son rôle dans la croissance.</p>
              <p className="text-sm text-emerald-400 mt-1">→ Cette gouvernance clarifie les rôles, les responsabilités et aligne tout le monde sur les mêmes objectifs.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Ce que cette gouvernance ne doit JAMAIS devenir */}
      <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-red-400" />
          Ce que cette gouvernance ne doit JAMAIS devenir
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Pour rester un levier de performance et d'alignement, cette gouvernance ne doit pas dériver.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4 font-semibold">
          Cette gouvernance ne doit pas être :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Un simple rapport mensuel</strong>
              <p className="text-sm text-gray-400 mt-1">Ce n'est pas juste des chiffres à consulter, mais un outil de décision et d'alignement pour toute l'équipe.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Une bureaucratie qui ralentit les décisions</strong>
              <p className="text-sm text-gray-400 mt-1">Elle doit simplifier et accélérer la prise de décision, pas créer des barrières.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Réservée uniquement à Martin</strong>
              <p className="text-sm text-gray-400 mt-1">Tous les 5 membres de l'équipe doivent la comprendre et l'utiliser pour s'aligner.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Statique ou figée</strong>
              <p className="text-sm text-gray-400 mt-1">Elle doit évoluer avec BZ Telecom, ses objectifs et les changements du marché télécom.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Trop technique ou jargonneuse</strong>
              <p className="text-sm text-gray-400 mt-1">Elle doit être pédagogique et accessible, tout en enseignant progressivement le jargon télécom nécessaire.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Stratégies 2026 pour reprendre des parts de marché */}
      <div className="bg-white/5 rounded-xl p-6 border border-orange-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-orange-400" />
          Stratégies 2026 pour reprendre des parts de marché
        </h4>
        <p className="text-gray-300 leading-relaxed mb-3">
          En 2026, nous innovons au niveau <strong className="text-white">marketing et ventes</strong> pour reprendre des parts de marché en téléphonie cloud.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Voici nos stratégies concrètes :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Renforcement de l'équipe ventes et développement d'affaires</strong>
              <p className="text-sm text-gray-400 mt-1">Félix Bourassa rejoint l'équipe avec plusieurs chapeaux : ventes, développement d'affaires, marketing. Son objectif : gagner plus de revenus en multipliant les opportunités commerciales.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Plus de présence sur les réseaux sociaux</strong>
              <p className="text-sm text-gray-400 mt-1">Plan marketing à bâtir de A à Z pour augmenter notre visibilité et notre notoriété dans le marché télécom québécois.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Nouveaux sites transactionnels</strong>
              <p className="text-sm text-gray-400 mt-1">Développement de plateformes web qui permettent aux clients de commander et configurer nos services directement en ligne.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Nouveaux produits téléphonie</strong>
              <p className="text-sm text-gray-400 mt-1">Innovation sur notre offre téléphonie Cloud Asterisk pour rester compétitifs et attirer de nouveaux clients.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Agents AI vocaux (innovation IA)</strong>
              <p className="text-sm text-gray-400 mt-1">Développement de robots vocaux intelligents (agents AI) à vendre comme nouveau produit. Ce n'est pas nécessairement connecté à notre système téléphonique actuel, mais ça utilise notre expertise vocale. C'est une nouvelle source de revenus qui reste dans notre domaine.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Partenariats contracteurs pour le câblage</strong>
              <p className="text-sm text-gray-400 mt-1">Nous avons déjà des partenariats qui fonctionnent bien. En 2026, nous en ajoutons de nouveaux pour augmenter le volume de projets câblage tout en maintenant notre qualité.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Objectifs macro 2026 */}
      <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-orange-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-orange-400" />
          Objectifs macro 2026
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          À l'horizon 2026, BZ Telecom doit reprendre des parts de marché en téléphonie cloud et continuer à croître en câblage.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4 font-semibold">
          Nos objectifs macro sont :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Reprendre des parts de marché en téléphonie cloud</strong>
              <p className="text-sm text-gray-400 mt-1">Compenser la baisse de 10% de 2025 et dépasser nos niveaux précédents grâce à l'innovation marketing, ventes et produits.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Augmenter le volume de câblage</strong>
              <p className="text-sm text-gray-400 mt-1">Développer de nouveaux partenariats contracteurs pour augmenter notre capacité tout en maintenant la qualité qui nous a permis de conserver nos acquis en 2025.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Suivre mensuellement les revenus et parts de marché</strong>
              <p className="text-sm text-gray-400 mt-1">Cette modal de gouvernance devient notre tableau de bord mensuel pour prendre des décisions éclairées basées sur des données réelles.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Lancer de nouveaux produits téléphonie et agents AI vocaux</strong>
              <p className="text-sm text-gray-400 mt-1">Innovation sur notre offre pour rester compétitifs et créer de nouvelles sources de revenus récurrents.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Structurer l'équipe pour la croissance</strong>
              <p className="text-sm text-gray-400 mt-1">Avec Félix en ventes/développement d'affaires, chaque membre de l'équipe (Martin, Benoit, Steven, Simon, Félix) comprend son rôle et contribue à la vision.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Développer notre présence marketing et digitale</strong>
              <p className="text-sm text-gray-400 mt-1">Réseaux sociaux, sites transactionnels, stratégie marketing complète pour augmenter notre visibilité et générer plus d'opportunités commerciales.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Phrase clé à retenir */}
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-orange-500/30">
        <div className="text-center">
          <h4 className="text-lg font-bold text-white mb-3 flex items-center justify-center gap-2">
            <Target className="w-5 h-5 text-orange-400" />
            Phrase clé à retenir
          </h4>
          <p className="text-xl font-semibold text-white leading-relaxed italic">
            Cette gouvernance n'existe pas pour compliquer.<br />
            Elle existe pour aligner, mesurer et faire grandir BZ Telecom ensemble.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 2 — CADRE HOLISTIQUE BZ TELECOM (COACHING)
// ============================================================================
function CadreTab() {
  const [expandedAxe, setExpandedAxe] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Cadre holistique BZ Telecom – Piliers de maturité (0–5)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Les 6 piliers BZ Telecom avec coaching détaillé pour chaque niveau de maturité (0-5)
        </p>
      </div>

      <div className="space-y-4">
        {piliersBZTelecom.map((axe) => (
          <div key={axe.id} className="bg-white/5 rounded-xl border border-orange-500/20 overflow-hidden">
            <button
              onClick={() => setExpandedAxe(expandedAxe === axe.id ? null : axe.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                  {axe.id}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white">{axe.nom}</h4>
                  {axe.avatars && axe.avatars.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {axe.avatars.map((avatarName, idx) => {
                        const avatarSrc = avatarMap[avatarName];
                        return avatarSrc ? (
                          avatarSrc === '$' ? (
                            <div 
                              key={idx}
                              className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 border border-orange-400 flex items-center justify-center text-white font-bold text-[10px] shadow-sm"
                              title={avatarName}
                            >
                              $
                            </div>
                          ) : avatarSrc === '👤' ? (
                            <div 
                              key={idx}
                              className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border border-orange-400 flex items-center justify-center text-white text-xs shadow-sm"
                              title={avatarName}
                            >
                              👤
                            </div>
                          ) : (
                            <img 
                              key={idx}
                              src={avatarSrc} 
                              alt={avatarName}
                              className="w-5 h-5 rounded-full border border-orange-400 object-contain bg-white/10"
                              title={avatarName}
                            />
                          )
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className={`transform transition-transform ${expandedAxe === axe.id ? 'rotate-180' : ''}`}>
                <X className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {expandedAxe === axe.id && (
              <div className="p-6 border-t border-indigo-500/20 space-y-4">
                {/* Intention */}
                <div>
                  <h5 className="text-sm font-bold text-orange-400 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Intention
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.intention}</p>
                </div>

                {/* Pourquoi critique */}
                <div>
                  <h5 className="text-sm font-bold text-yellow-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Pourquoi c'est critique
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.pourquoiCritique}</p>
                </div>

                {/* Concrètement BZ */}
                <div>
                  <h5 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Concrètement chez BZ
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.concretementBZ}</p>
                </div>

                {/* Erreurs fréquentes */}
                <div>
                  <h5 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Erreurs fréquentes
                  </h5>
                  <ul className="space-y-2">
                    {axe.erreursFrequentes.map((erreur, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                        <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <span>{erreur}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Niveaux de maturité */}
                <div>
                  <h5 className="text-sm font-bold text-orange-400 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Niveaux 0 → 5
                  </h5>
                  <div className="space-y-2">
                    {axe.niveaux.map((n) => (
                      <div key={n.niveau} className="bg-slate-800/50 rounded-lg p-3 border border-indigo-500/20">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-orange-400">{n.niveau}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs font-semibold text-purple-300">{n.label}</span>
                        </div>
                        <p className="text-gray-300 text-xs leading-relaxed">{n.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* À faire maintenant */}
                {axe.aFaireMaintenant && axe.aFaireMaintenant.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-blue-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      À faire maintenant (N+1)
                    </h5>
                    <ul className="space-y-2">
                      {axe.aFaireMaintenant.map((action, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preuves visibles */}
                {axe.preuvesVisibles && axe.preuvesVisibles.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Preuves visibles
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {axe.preuvesVisibles.map((preuve, idx) => (
                        <span key={idx} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded border border-emerald-500/30">
                          {preuve}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Phrase de clôture */}
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-purple-500/30">
        <div className="text-center">
          <p className="text-lg font-semibold text-white leading-relaxed italic">
            Chaque pilier BZ Telecom est une responsabilité collective,<br />
            mais une redevabilité claire.<br />
            La maturité ne s'improvise pas, elle se gouverne.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 3 — VUE MATURITÉ
// ============================================================================
function MaturiteTab() {
  // Convertir pour le graphique radar (échelle 0-5 vers 0-100)
  const chartData = maturiteData.map(item => ({
    category: item.axe.length > 15 ? item.axe.substring(0, 15) + '...' : item.axe,
    Actuel: (item.actuel / 5) * 100,
    Cible: (item.cible / 5) * 100,
  }));

  const actuelAverage = (maturiteData.reduce((sum, item) => sum + item.actuel, 0) / maturiteData.length).toFixed(1);
  const cibleAverage = (maturiteData.reduce((sum, item) => sum + item.cible, 0) / maturiteData.length).toFixed(1);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Maturité (Spider + Bar)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Évaluation de la maturité actuelle et cible pour fin 2026 — Niveaux 0-5 (style ITIL)
        </p>
      </div>

      {/* Scores moyens */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-300 font-medium">Score Actuel (AS-IS)</div>
          <div className="text-2xl font-bold text-yellow-200">{actuelAverage}/5</div>
          <div className="text-xs text-yellow-400">État actuel</div>
        </div>
        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Score Cible (TARGET)</div>
          <div className="text-2xl font-bold text-emerald-200">{cibleAverage}/5</div>
          <div className="text-xs text-emerald-400">Objectif fin 2026</div>
        </div>
      </div>

      {/* Graphique Spider */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Graphique Radar - Maturité PRIsME</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
              <PolarGrid stroke="#4b5563" />
              <PolarAngleAxis 
                dataKey="category" 
                tick={{ fontSize: 11, fill: '#d1d5db' }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickCount={6}
              />
              
              <Radar
                name="Actuel (AS-IS)"
                dataKey="Actuel"
                stroke="#fbbf24"
                fill="#fbbf24"
                fillOpacity={0.2}
                strokeWidth={3}
                dot={{ fill: '#fbbf24', strokeWidth: 2, r: 4 }}
              />
              
              <Radar
                name="Cible (TARGET)"
                dataKey="Cible"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.1}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
              />
              
              <Legend 
                wrapperStyle={{ paddingTop: '20px', color: '#d1d5db' }}
                iconType="circle"
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Bar Chart - Détail par axe</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={maturiteData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
              <XAxis 
                dataKey="axe" 
                angle={-45} 
                textAnchor="end" 
                height={120}
                tick={{ fontSize: 10, fill: '#d1d5db' }}
              />
              <YAxis 
                domain={[0, 5]}
                tick={{ fontSize: 10, fill: '#d1d5db' }}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px' }}
                labelStyle={{ color: '#d1d5db' }}
              />
              <Legend />
              <Bar dataKey="actuel" fill="#fbbf24" name="Actuel (AS-IS)" />
              <Bar dataKey="cible" fill="#10b981" name="Cible (TARGET)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Échelle */}
      <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
        <h4 className="text-lg font-bold text-white mb-3">Échelle de maturité</h4>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-center gap-2">
            <span className="font-semibold text-orange-400">0 =</span>
            <span>Inexistant</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-orange-400">1 =</span>
            <span>POC / Expérimentation</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-orange-400">2 =</span>
            <span>Adoption structurée</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-orange-400">3 =</span>
            <span>Maturité opérationnelle</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-orange-400">4 =</span>
            <span>Excellence</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-orange-400">5 =</span>
            <span>Référence marché</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 4 — RÔLES & RESPONSABILITÉS (RACI)
// ============================================================================
function RACITab() {
  const raciColors = {
    R: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
    A: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300',
    C: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300',
    I: 'bg-purple-500/20 border-purple-500/50 text-purple-300'
  };

  const raciLabels = {
    R: 'Responsable (fait le travail)',
    A: 'Accountable (décide et répond)',
    C: 'Consulté (donne son avis)',
    I: 'Informé (est tenu au courant)'
  };

  const [showGuide, setShowGuide] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{
    activite: string;
    role: string;
    raci: 'R' | 'A' | 'C' | 'I';
  } | null>(null);

  const raciHolisticText: Record<'R' | 'A' | 'C' | 'I', string> = {
    R: 'Responsable (R) = tu exécutes concrètement le travail pour cette activité. Tu t’assures que les tâches avancent, que la qualité est au rendez-vous et que les livrables sont prêts dans les délais.',
    A: 'Accountable (A) = tu es imputable du résultat pour cette activité. Même si d’autres exécutent, c’est toi qui “portes” le sujet, qui tranches en cas de doute et qui répond des résultats devant la direction.',
    C: 'Consulté (C) = on vient te voir AVANT de décider ou d’agir. Tu donnes ton avis, ton expertise, tes mises en garde pour éviter les angles morts et améliorer la décision.',
    I: 'Informé (I) = tu n’es pas dans l’exécution ou la décision, mais tu dois être au courant de ce qui se passe pour rester aligné et éviter les surprises.'
  };

  // Extraire tous les rôles uniques et les trier (personnes avant groupes)
  const allRoles = new Set<string>();
  raciData.forEach(activity => {
    Object.keys(activity.roles).forEach(role => allRoles.add(role));
  });
  
  const groupes = ['Comité PRIsME', 'Directeurs de compte', 'Gestionnaire', 'Primes'];
  const personnes = Array.from(allRoles).filter(role => !groupes.includes(role));
  const groupesPresents = Array.from(allRoles).filter(role => groupes.includes(role));
  
  // Trier les personnes : Dany (1er), Éric (2e), puis les autres par ordre alphabétique
  const personnesTriees = personnes.sort();
  const personnesOrdonnees: string[] = [];
  
  // Dany en 1er
  if (personnesTriees.includes('Dany Bourassa')) {
    personnesOrdonnees.push('Dany Bourassa');
  }
  
  // Éric en 2e
  if (personnesTriees.includes('Éric Hains')) {
    personnesOrdonnees.push('Éric Hains');
  }
  
  // Les autres personnes triées alphabétiquement
  const autresPersonnes = personnesTriees.filter(p => p !== 'Dany Bourassa' && p !== 'Éric Hains');
  personnesOrdonnees.push(...autresPersonnes.sort());
  
  // Ajouter les groupes (triés)
  const uniqueRoles = [...personnesOrdonnees, ...groupesPresents.sort()];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-white mb-1 md:mb-0 text-center md:text-left">
              Rôles & responsabilités (RACI) — BZ Telecom
            </h3>
            <p className="text-gray-300 text-sm text-center md:text-left">
              Matrice RACI pour la gouvernance BZ Telecom (qui est A, qui est R, qui est consulté ou informé par activité).
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowGuide(prev => !prev)}
            className="inline-flex items-center justify-center px-4 py-2 rounded-full border border-orange-400/70 bg-black/20 hover:bg-orange-500/20 text-sm font-medium text-orange-200 shadow-sm transition-colors"
          >
            Guide pratique du RACI
          </button>
        </div>
      </div>

      {showGuide && (
        <div className="bg-slate-900/70 rounded-xl border border-orange-500/40 p-5 space-y-3">
          <h4 className="text-lg font-bold text-white">Comment lire et utiliser le RACI chez BZ Telecom</h4>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-200">
            <div className="space-y-2">
              <p className="font-semibold text-orange-200">Bonnes pratiques :</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Idéalement <strong>1 seul A</strong> par activité : on sait clairement qui “porte” le sujet.</li>
                <li>Il peut y avoir <strong>plusieurs R</strong>, mais chacun sait ce qu’il doit livrer.</li>
                <li>Les personnes en C doivent être consultées <strong>avant</strong> les décisions importantes.</li>
                <li>Les personnes en I reçoivent un <strong>résumé clair</strong> à la fin (pas tous les détails techniques).</li>
                <li>RACI sert à clarifier les attentes, pas à créer de la bureaucratie.</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-red-200">À éviter :</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Mettre tout le monde en A ou en R : plus personne ne sait qui décide ou qui livre.</li>
                <li>Confondre <strong>A</strong> avec “patron hiérarchique” : A = imputable sur cette activité précise.</li>
                <li>Oublier de tenir les personnes en I au courant : ça crée du bruit, des surprises et du désalignement.</li>
                <li>Utiliser le RACI pour blâmer : c’est un outil de clarté et de coaching, pas de reproche.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Légende RACI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(raciLabels).map(([key, label]) => (
          <div key={key} className={`${raciColors[key as keyof typeof raciColors]} rounded-lg p-3 border-2 text-center`}>
            <div className="font-bold text-lg mb-1">{key}</div>
            <div className="text-xs">{label}</div>
          </div>
        ))}
      </div>

      {/* Tableau RACI */}
      <div className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-indigo-500/30">
              <th className="text-left p-4 text-white font-bold">Activité</th>
              {uniqueRoles.map(role => (
                <th key={role} className="text-center p-4 text-white font-bold min-w-[120px]">
                  <div className="flex flex-col items-center gap-2">
                    {avatarMap[role] ? (
                      avatarMap[role] === '$' ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 border-2 border-orange-400 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                          $
                        </div>
                      ) : avatarMap[role] === '👤' ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-orange-400 flex items-center justify-center text-white text-lg shadow-lg">
                          👤
                        </div>
                      ) : (
                        <img 
                          src={avatarMap[role]} 
                          alt={role}
                          className="w-8 h-8 rounded-full border-2 border-orange-400 object-contain bg-white/10"
                        />
                      )
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 border-2 border-orange-400 flex items-center justify-center text-xs text-white">
                        {role.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                    )}
                    <span className="text-xs">{role}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {raciData.map((activity, idx) => (
              <tr key={idx} className="border-b border-indigo-500/20 hover:bg-white/5">
                <td className="p-4 text-gray-300 font-medium">{activity.activite}</td>
                {uniqueRoles.map(role => {
                  const raciValue = (activity.roles as unknown as Record<string, string>)[role] || '';
                  return (
                    <td key={role} className="text-center p-4">
                      {raciValue && (
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedCell({
                              activite: activity.activite,
                              role,
                              raci: raciValue as 'R' | 'A' | 'C' | 'I'
                            })
                          }
                          className={`inline-block px-3 py-1 rounded-full font-bold text-sm border-2 cursor-pointer hover:scale-105 transition-transform ${raciColors[raciValue as keyof typeof raciColors]}`}
                          title={`Cliquer pour voir ton rôle (${raciValue}) sur cette activité`}
                        >
                          {raciValue}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCell && (
        <div className="bg-slate-900/70 rounded-xl border border-orange-500/40 p-5 space-y-3">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h4 className="text-lg font-bold text-white mb-1">
                Ton rôle sur&nbsp;
                <span className="text-orange-300">« {selectedCell.activite} »</span>
              </h4>
              <p className="text-sm text-gray-300 mb-2">
                <span className="font-semibold text-white">{selectedCell.role}</span> est en position&nbsp;
                <span className="font-semibold text-orange-300">{selectedCell.raci}</span> pour cette activité.
              </p>
              <p className="text-sm text-gray-200 leading-relaxed">
                {raciHolisticText[selectedCell.raci]}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedCell(null)}
              className="text-xs text-gray-400 hover:text-white underline mt-1"
            >
              Fermer
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Astuce : utilise ce bloc pour expliquer à ton équipe ce que tu attends concrètement d’eux sur cette activité
            (ex. exemples de décisions, livrables, comportements attendus).
          </p>
        </div>
      )}

      {/* Rôles spécifiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/LogoPrismeSmall.png" alt="Comité" className="w-8 h-8 rounded-full border-2 border-orange-400 object-contain bg-white/10" />
            <h4 className="text-lg font-bold text-white">Rôle du Comité PRIsME</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            Le Comité PRIsME se réunit 2 fois par mois. Dany participe 1 fois par mois. Les décisions sont prises collectivement, avec Francis comme facilitateur des réunions.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/EH.png" alt="Éric Hains" className="w-8 h-8 rounded-full border-2 border-orange-400" />
            <h4 className="text-lg font-bold text-white">Rôle de l'Owner</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            L'Owner (<strong className="text-white">Éric Hains</strong>) est responsable de l'exécution opérationnelle, de la qualité des livraisons PRIsME Client et de l'adoption par les Primes.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/DB.png" alt="Dany Bourassa" className="w-8 h-8 rounded-full border-2 border-orange-400" />
            <h4 className="text-lg font-bold text-white">Rôle du PRIsME Master</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            Le PRIsME Master (<strong className="text-white">Dany Bourassa</strong>) définit la vision stratégique, valide les décisions majeures et garantit l'alignement. Effort décroissant : 20h/sem → 10h en mars → 5h en juin.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/JP.png" alt="Jean-Pierre Leclerc" className="w-8 h-8 rounded-full border-2 border-orange-400" />
            <h4 className="text-lg font-bold text-white">Jean-Pierre Leclerc</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Focus : Qualité/KPI + PRIsME Employé</strong>. <span className="text-red-300">Ne pas impliquer dans PRIsME Client</span>. Responsable de la mesure, du suivi qualité et du déploiement PRIsME Employé.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/FM.png" alt="Francis Marquis" className="w-8 h-8 rounded-full border-2 border-orange-400" />
            <h4 className="text-lg font-bold text-white">Francis Marquis</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Facilitateur des réunions</strong> + gouvernance. Anime les 2 rencontres mensuelles du Comité PRIsME.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/PLF.png" alt="Pierre-Luc Fortin" className="w-8 h-8 rounded-full border-2 border-orange-400" />
            <h4 className="text-lg font-bold text-white">Pierre-Luc Fortin</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Communication interne + Commercialisation</strong> (stratégie PULL) + gestion documentaire PRIsME.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/JMC.png" alt="Jean-Michel Caouette" className="w-8 h-8 rounded-full border-2 border-orange-400" />
            <h4 className="text-lg font-bold text-white">Jean-Michel Caouette</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Innovation/Exécution + Documentation</strong>. Porte les initiatives d'innovation et la documentation PRIsME.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 7 — RÉUNIONS (MOKUP STRUCTURÉ)
// ============================================================================

type ReunionSubTab = 'infos' | 'ordre' | 'notes' | 'actions' | 'ai';

const premiereReunion = {
  titre: 'Lancement gouvernance BZ Telecom 2026',
  date: '2026-02-15',
  duree: '90 minutes',
  objectif: 'Présenter et aligner l’équipe BZ Telecom sur la nouvelle gouvernance, le cadre holistique (6 piliers), le RACI et la roadmap 2026.',
  participants: [
    { nom: 'Martin Carrier', role: 'Président BZ Telecom (A global)' },
    { nom: 'Benoit Dorval', role: 'Directeur câblage' },
    { nom: 'Steven Vallée-Barette', role: 'Téléphonie & innovation' },
    { nom: 'Simon Cantin', role: 'Chef d’équipe câblage' },
    { nom: 'Félix Bourassa', role: 'Ventes & développement des affaires' },
    { nom: 'Dany Bourassa', role: 'Coach gouvernance externe / animateur' }
  ],
  ordreDuJour: [
    {
      titre: '1. Ouverture & intention de la rencontre',
      duree: '10 min',
      details:
        'Clarifier pourquoi on se rencontre, ce qu’on veut obtenir à la fin de la réunion et comment on va travailler ensemble (ton, ouverture, droit de parole).'
    },
    {
      titre: '2. Présentation de la gouvernance BZ Telecom 2026',
      duree: '15 min',
      details:
        'Revenir sur l’onglet « Esprit & Objectifs macro » : vision 2026, enjeux de revenus/parts de marché, rôle du câblage et de la téléphonie, place de l’IA (BOT vocaux).'
    },
    {
      titre: '3. Cadre holistique BZ Telecom (6 piliers)',
      duree: '25 min',
      details:
        'Passer en revue les 6 piliers : Gouvernance & stratégie, Ventes/marketing, Produits téléphonie & innovation, Câblage, Service client, Administration & infrastructure. Discuter des niveaux de maturité actuels vs souhaités.'
    },
    {
      titre: '4. Rôles & responsabilités (RACI)',
      duree: '15 min',
      details:
        'Expliquer le RACI, regarder ensemble qui est A, R, C, I par activité clé et valider que chacun comprend bien son rôle.'
    },
    {
      titre: '5. Roadmap 2026 BZ Telecom (haut niveau)',
      duree: '15 min',
      details:
        'Présenter les grandes étapes 2026 (trimestres), les priorités par pilier et les liens avec la réalité de l’équipe (charge, capacité, risques).'
    },
    {
      titre: '6. Tour de table : questions, craintes, attentes',
      duree: '10 min',
      details:
        'Donner la parole à chacun pour exprimer ses questions, préoccupations, attentes par rapport à la gouvernance et au cadre proposé.'
    },
    {
      titre: '7. Prochaines actions & rituels de gouvernance',
      duree: '10 min',
      details:
        'Confirmer la cadence des rencontres BZ Telecom, les prochains livrables (ajustements RACI, documentation, suivis de maturité) et les responsables.'
    }
  ],
  notesTemplate: {
    decisions: [
      'Décisions clés prises pendant la rencontre (ex. validation des 6 piliers, approbation du RACI, cadence des rencontres).'
    ],
    enjeux: [
      'Enjeux identifiés (capacités, risques, besoin de formation, points à creuser plus tard).'
    ],
    parking: [
      'Sujets importants mais hors scope de cette rencontre, à planifier dans une prochaine réunion.'
    ]
  },
  actionsTemplate: [
    {
      texte: 'Finaliser et partager la version 1 du RACI BZ Telecom à toute l’équipe.',
      responsable: 'Dany / Martin',
      echeance: 'Dans la semaine suivant la rencontre'
    },
    {
      texte: 'Adapter les 6 piliers en version « simplifiée » pour l’équipe terrain (résumé visuel ou 1-pager).',
      responsable: 'Martin / Félix',
      echeance: 'Fin du mois'
    },
    {
      texte: 'Planifier les 3 prochaines rencontres de gouvernance BZ Telecom dans les agendas.',
      responsable: 'Martin / Émilie',
      echeance: 'Avant la fin de la semaine'
    }
  ]
};

function ReunionsTab() {
  const [activeSubTab, setActiveSubTab] = useState<ReunionSubTab>('infos');

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Réunions BZ Telecom
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Structure de réunion complète pour piloter la gouvernance, des infos de base jusqu’au résumé AI.
        </p>
      </div>

      {/* Onglets internes de la réunion */}
      <div className="border-b border-indigo-500/30 bg-slate-900/50 px-4 pt-2 pb-0 overflow-x-auto rounded-t-xl">
        <div className="flex gap-2">
          {[
            { id: 'infos' as ReunionSubTab, label: 'Infos & participants' },
            { id: 'ordre' as ReunionSubTab, label: 'Ordre du jour' },
            { id: 'notes' as ReunionSubTab, label: 'Notes, décisions & enjeux' },
            { id: 'actions' as ReunionSubTab, label: 'Actions / TODO' },
            { id: 'ai' as ReunionSubTab, label: 'Résumé AI + verbatim' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`px-4 py-2 rounded-t-lg text-xs md:text-sm font-semibold whitespace-nowrap transition-all ${
                activeSubTab === tab.id
                  ? 'bg-gradient-to-b from-orange-600 to-orange-700 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white/5 rounded-b-xl rounded-tr-xl border border-indigo-500/20 p-4 space-y-4">
        {activeSubTab === 'infos' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-lg font-bold text-white mb-1">
                {premiereReunion.titre}
              </h4>
              <p className="text-sm text-gray-300">
                Date cible : <span className="text-orange-300">{premiereReunion.date}</span> · Durée prévue :{' '}
                <span className="text-orange-300">{premiereReunion.duree}</span>
              </p>
            </div>
            <div className="bg-slate-900/60 rounded-lg p-4 border border-indigo-500/30">
              <h5 className="text-sm font-bold text-white mb-2">Intention de la réunion</h5>
              <p className="text-sm text-gray-300 leading-relaxed">
                {premiereReunion.objectif}
              </p>
            </div>
            <div>
              <h5 className="text-sm font-bold text-white mb-2">Participants</h5>
              <div className="grid md:grid-cols-2 gap-3">
                {premiereReunion.participants.map((p) => (
                  <div
                    key={p.nom}
                    className="flex items-center gap-3 bg-slate-900/60 rounded-lg p-3 border border-indigo-500/20"
                  >
                    <img
                      src={avatarMap[p.nom]}
                      alt={p.nom}
                      className="w-8 h-8 rounded-full border-2 border-orange-400 object-contain bg-white/10"
                    />
                    <div>
                      <div className="text-sm font-semibold text-white">{p.nom}</div>
                      <div className="text-xs text-gray-300">{p.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeSubTab === 'ordre' && (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Ordre du jour — {premiereReunion.titre}</h4>
            <p className="text-sm text-gray-300">
              Objectif : couvrir la gouvernance, le cadre holistique (6 piliers), le RACI et la roadmap 2026 de façon
              claire et pédagogique pour toute l’équipe.
            </p>
            <ol className="space-y-3 text-sm text-gray-200 list-decimal list-inside">
              {premiereReunion.ordreDuJour.map((item) => (
                <li key={item.titre}>
                  <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1">
                    <span className="font-semibold text-white">{item.titre}</span>
                    <span className="text-xs text-orange-300 md:ml-4">{item.duree}</span>
                  </div>
                  <p className="text-xs text-gray-300 mt-1">{item.details}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        {activeSubTab === 'notes' && (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Notes, décisions & enjeux (gabarit)</h4>
            <p className="text-sm text-gray-300">
              Tout est en mode maquette (hardcodé). Plus tard, on pourra connecter ces sections à une vraie base de
              données ou à un assistant AI.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-slate-900/70 rounded-lg p-3 border border-emerald-500/30">
                <h5 className="font-bold text-emerald-300 mb-2">Décisions</h5>
                <ul className="list-disc list-inside space-y-1 text-gray-200">
                  {premiereReunion.notesTemplate.decisions.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-900/70 rounded-lg p-3 border border-orange-500/30">
                <h5 className="font-bold text-orange-300 mb-2">Enjeux / risques</h5>
                <ul className="list-disc list-inside space-y-1 text-gray-200">
                  {premiereReunion.notesTemplate.enjeux.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-900/70 rounded-lg p-3 border border-indigo-500/30">
                <h5 className="font-bold text-indigo-300 mb-2">Parking lot</h5>
                <ul className="list-disc list-inside space-y-1 text-gray-200">
                  {premiereReunion.notesTemplate.parking.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === 'actions' && (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Actions / TODO (maquette)</h4>
            <p className="text-sm text-gray-300">
              Liste d’actions structurée avec responsable, échéance cible et lien implicite avec les piliers et la
              gouvernance.
            </p>
            <div className="space-y-2 text-sm">
              {premiereReunion.actionsTemplate.map((a, idx) => (
                <div
                  key={`${a.texte}-${idx}`}
                  className="bg-slate-900/70 rounded-lg p-3 border border-indigo-500/20 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                >
                  <div>
                    <p className="text-gray-200">{a.texte}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Responsable : <span className="text-orange-300">{a.responsable}</span>
                    </p>
                  </div>
                  <div className="text-xs text-gray-300 md:text-right">
                    Échéance cible : <span className="text-emerald-300">{a.echeance}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSubTab === 'ai' && (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Résumé AI + verbatim (maquette)</h4>
            <p className="text-sm text-gray-300">
              Plus tard, cette section pourra être alimentée automatiquement par un assistant AI qui résume la réunion
              et extrait les points clés. Pour l’instant, on affiche un exemple de structure.
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-900/70 rounded-lg p-3 border border-emerald-500/30">
                <h5 className="font-bold text-emerald-300 mb-2">Résumé AI (exemple)</h5>
                <ul className="list-disc list-inside space-y-1 text-gray-200">
                  <li>La gouvernance BZ Telecom 2026 est validée par l’équipe, avec 6 piliers clairs et un RACI associé.</li>
                  <li>Le câblage reste un levier clé de croissance, mais la priorité 2026 est de regagner des parts de marché en téléphonie cloud.</li>
                  <li>Les rôles de chacun (Martin, Benoit, Steven, Simon, Félix, Dany) sont mieux compris et acceptés.</li>
                  <li>Une cadence de rencontres de gouvernance BZ Telecom est acceptée par tous.</li>
                </ul>
              </div>
              <div className="bg-slate-900/70 rounded-lg p-3 border border-indigo-500/30">
                <h5 className="font-bold text-indigo-300 mb-2">Verbatim / notes brutes (exemple)</h5>
                <p className="text-xs text-gray-300 leading-relaxed">
                  « On n’a jamais eu ce niveau de clarté sur qui fait quoi. » — « Il faudra revenir souvent sur les
                  indicateurs pour ne pas perdre le focus revenus/qualité. » — « Le RACI nous sort de l’ambiguïté et
                  permet de parler des vraies attentes sans attaquer personne. »
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
// ============================================================================
// ONGLET 5 — ROADMAP ANNUELLE 2026
// ============================================================================
function RoadmapTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Roadmap 2026
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Planification trimestrielle par axe PRIsME — Intentions + livrables structurants
        </p>
      </div>

      {/* Résumé effort par personne */}
      {(() => {
        // Calculer le total d'heures par personne
        const effortParPersonne: Record<string, number> = {};
        
        roadmap2026.forEach(quarter => {
          quarter.axes.forEach(axe => {
            // Récupérer les personnes A et R pour cet axe
            const mapping: Record<string, string> = {
              'Livraison & Exécution': 'Livraison PRIsME Client',
              'Maturité globale': 'Qualité, KPI & Suivi'
            };
            const nomRACI = mapping[axe.axe] || axe.axe;
            const activite = raciData.find(a => a.activite === nomRACI);
            
            if (activite) {
              const personnesAR: string[] = [];
              Object.entries(activite.roles).forEach(([nom, role]) => {
                if ((role === 'A' || role === 'R') && !['Comité PRIsME', 'Primes', 'Directeurs de compte', 'Gestionnaire'].includes(nom)) {
                  personnesAR.push(nom);
                }
              });
              
              // Répartir l'effort : A prend 60%, R partagent les 40% (divisé par nombre de R)
              const personnesA = personnesAR.filter(nom => (activite.roles as unknown as Record<string, string>)[nom] === 'A');
              const personnesR = personnesAR.filter(nom => (activite.roles as unknown as Record<string, string>)[nom] === 'R');
              
              axe.livrables.forEach((livrable: any) => {
                const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                if (effort > 0 && personnesAR.length > 0) {
                  // Répartition : A = 60% / nombre de A, R = 40% / nombre de R
                  const effortA = personnesA.length > 0 ? (effort * 0.6) / personnesA.length : 0;
                  const effortR = personnesR.length > 0 ? (effort * 0.4) / personnesR.length : 0;
                  
                  personnesA.forEach(nom => {
                    effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortA;
                  });
                  personnesR.forEach(nom => {
                    effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortR;
                  });
                }
              });
            }
          });
        });
        
        const personnesTriees = Object.entries(effortParPersonne)
          .sort((a, b) => b[1] - a[1])
          .filter(([_, effort]) => effort > 0);
        
        return (
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-6 border-2 border-indigo-500/30 mb-6">
            <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Target className="w-6 h-6 text-orange-400" />
              Répartition effort 2026 par personne
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {personnesTriees.map(([nom, effort]) => {
                const avatarSrc = avatarMap[nom];
                return (
                  <div key={nom} className="bg-slate-900/50 rounded-lg p-3 border border-indigo-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      {avatarSrc ? (
                        avatarSrc === '$' || avatarSrc === '👤' ? (
                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300 cursor-help" title={nom}>
                            {avatarSrc}
                          </div>
                        ) : (
                          <img 
                            src={avatarSrc} 
                            alt={nom} 
                            title={nom}
                            className="w-8 h-8 rounded-full border-2 border-indigo-500/50 object-cover cursor-help"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        )
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white cursor-help" title={nom}>
                          {nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-300 truncate">{nom.split(' ')[0]}</p>
                        <p className="text-xs text-gray-400 truncate">{nom.split(' ').slice(1).join(' ')}</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-300">{Math.round(effort)}</p>
                      <p className="text-xs text-gray-400">heures</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-indigo-500/20">
              <p className="text-sm text-gray-400">
                <strong className="text-white">Total : {Math.round(Object.values(effortParPersonne).reduce((a, b) => a + b, 0))} heures</strong> réparties sur l'année 2026
              </p>
            </div>
          </div>
        );
      })()}

      <div className="space-y-6">
        {roadmap2026.map((quarter, qIdx) => {
          const colorMap = {
            'Q1 2026': 'from-indigo-500/20 to-indigo-600/20 border-indigo-500/30',
            'Q2 2026': 'from-purple-500/20 to-purple-600/20 border-purple-500/30',
            'Q3 2026': 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
            'Q4 2026': 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30'
          };
          const bgClass = colorMap[quarter.trimestre as keyof typeof colorMap];

          return (
            <div key={qIdx} className={`bg-gradient-to-br ${bgClass} rounded-xl p-6 border-2`}>
              <div className="mb-4">
                <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  {quarter.trimestre}
                </h4>
                {quarter.intention && (
                  <p className="text-sm text-gray-300 italic ml-8">{quarter.intention}</p>
                )}
              </div>
              <div className="space-y-4">
                {quarter.axes.map((axe, aIdx) => {
                  // Récupérer les personnes A et R pour cet axe selon le RACI
                  const getPersonnesAR = (axeName: string) => {
                    // Mapping entre noms de la roadmap et noms du RACI
                    const mapping: Record<string, string> = {
                      'Livraison & Exécution': 'Livraison PRIsME Client', // Utiliser Livraison PRIsME Client comme référence
                      'Maturité globale': 'Qualité, KPI & Suivi' // Utiliser Qualité, KPI & Suivi comme référence
                    };
                    
                    const nomRACI = mapping[axeName] || axeName;
                    const activite = raciData.find(a => a.activite === nomRACI);
                    if (!activite) return [];
                    
                    const personnes: Array<{ nom: string; role: 'A' | 'R' | 'C' }> = [];
                    Object.entries(activite.roles).forEach(([nom, role]) => {
                      // Pour PRIsME Employé et Département, inclure aussi Dany (C)
                      if (nomRACI === 'PRIsME Employé et Département' && nom === 'Dany Bourassa') {
                        personnes.push({ nom, role: 'C' as 'A' | 'R' | 'C' });
                      } else if ((role === 'A' || role === 'R') && !['Comité PRIsME', 'Primes', 'Directeurs de compte', 'Gestionnaire'].includes(nom)) {
                        personnes.push({ nom, role: role as 'A' | 'R' | 'C' });
                      }
                    });
                    
                    // Pour Livraison & Exécution, ajouter Dany Bourassa comme A (pilotage projet avec Éric)
                    if (axeName === 'Livraison & Exécution' && !personnes.find(p => p.nom === 'Dany Bourassa')) {
                      personnes.push({ nom: 'Dany Bourassa', role: 'A' });
                    }
                    return personnes;
                  };
                  
                  const personnesAR = getPersonnesAR(axe.axe);
                  
                  // Calculer le total d'heures pour cet axe
                  const totalEffortAxe = axe.livrables.reduce((total: number, livrable: any) => {
                    const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                    return total + effort;
                  }, 0);
                  
                  return (
                    <div key={aIdx} className="bg-slate-900/50 rounded-lg p-4 border border-indigo-500/20">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="text-lg font-bold text-indigo-300 mb-1">{axe.axe}</h5>
                          {axe.intention && (
                            <p className="text-xs text-gray-400 italic mb-2">{axe.intention}</p>
                          )}
                        </div>
                        {totalEffortAxe > 0 && (
                          <div className="flex-shrink-0 ml-3">
                            <div className="bg-indigo-500/20 border border-indigo-500/50 rounded-lg px-3 py-1.5">
                              <p className="text-xs text-gray-400 text-center">Total</p>
                              <p className="text-lg font-bold text-indigo-300 text-center">{totalEffortAxe}h</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-indigo-500/20">
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300">Livrable</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-32">Responsables</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-24">Priorité</th>
                              <th className="text-right py-2 px-3 text-xs font-bold text-indigo-300 w-24">Effort (h)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {axe.livrables.map((livrable: any, lIdx: number) => {
                              const texte = typeof livrable === 'string' ? livrable : livrable.texte;
                              const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                              const priorite = typeof livrable === 'string' ? 'Moyenne' : livrable.priorite || 'Moyenne';
                              
                              const prioriteColors: Record<string, string> = {
                                'Haute': 'bg-red-500/20 text-red-300 border-red-500/50',
                                'Moyenne': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
                                'Basse': 'bg-green-500/20 text-green-300 border-green-500/50'
                              };
                              
                              return (
                                <tr key={lIdx} className="border-b border-indigo-500/10 hover:bg-white/5">
                                  <td className="py-2 px-3">
                                    <div className="flex items-start gap-2 text-gray-300 text-sm">
                                      <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                                      <span>{texte}</span>
                                    </div>
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    {personnesAR.length > 0 ? (
                                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                        {personnesAR.map((personne, pIdx) => {
                                          const avatarSrc = avatarMap[personne.nom];
                                          return (
                                            <div key={pIdx} className="flex items-center gap-1" title={personne.nom}>
                                              {avatarSrc ? (
                                                avatarSrc === '$' || avatarSrc === '👤' ? (
                                                  <div className="w-6 h-6 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300" title={personne.nom}>
                                                    {avatarSrc}
                                                  </div>
                                                ) : (
                                                  <img 
                                                    src={avatarSrc} 
                                                    alt={personne.nom} 
                                                    title={personne.nom}
                                                    className="w-6 h-6 rounded-full border-2 border-indigo-500/50 object-cover cursor-help"
                                                    onError={(e) => {
                                                      const target = e.target as HTMLImageElement;
                                                      target.style.display = 'none';
                                                    }}
                                                  />
                                                )
                                              ) : (
                                                <div className="w-6 h-6 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white cursor-help" title={personne.nom}>
                                                  {personne.nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                </div>
                                              )}
                                              <span className={`text-xs font-bold px-1 py-0.5 rounded cursor-help ${
                                                personne.role === 'A' 
                                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50' 
                                                  : personne.role === 'R'
                                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                                                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                                              }`} title={`${personne.nom} - ${personne.role === 'A' ? 'Accountable' : personne.role === 'R' ? 'Responsable' : 'Consulté'}`}>
                                                {personne.role}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    <span className={`text-xs font-bold px-2 py-1 rounded border ${prioriteColors[priorite] || prioriteColors['Moyenne']}`}>
                                      {priorite}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-right">
                                    {effort > 0 ? (
                                      <span className="text-sm font-semibold text-indigo-300">{effort}h</span>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          {totalEffortAxe > 0 && (
                            <tfoot>
                              <tr className="border-t-2 border-indigo-500/30 bg-indigo-500/10">
                                <td className="py-2 px-3 text-right font-bold text-indigo-300" colSpan={3}>
                                  Total {axe.axe}
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <span className="text-lg font-bold text-indigo-300">{totalEffortAxe}h</span>
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Focus PME */}
      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-6 border-2 border-purple-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-orange-400" />
          Focus PME 2026
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <h5 className="text-sm font-bold text-purple-300 mb-2">~50 livrables structurants</h5>
            <p className="text-gray-300 text-sm">
              Livrables alignés avec les 10 axes PRIsME, priorisés selon l'impact business et la faisabilité technique.
            </p>
          </div>
          <div>
            <h5 className="text-sm font-bold text-purple-300 mb-2">20 no-brainers</h5>
            <p className="text-gray-300 text-sm">
              Actions rapides à haut impact : documentation, formations, intégrations simples, améliorations UX.
            </p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-purple-500/30">
          <h5 className="text-sm font-bold text-purple-300 mb-3">Éléments clés mis en évidence dans la roadmap :</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span><strong className="text-white">PRIsME Employé</strong> — Déploiement complet</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span><strong className="text-white">Outil projet intégré</strong> (inspiré PMBOK)</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span><strong className="text-white">Catalogue d'actifs</strong> — Recherche performante</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span><strong className="text-white">BOT PRIsME</strong> — Automatisation opérationnelle</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 6 — GOUVERNANCE & RITUELS
// ============================================================================
function RituelsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Gouvernance & rituels
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Cadence, règles de décision et mécanismes de gouvernance
        </p>
      </div>

      {/* Cadence des rencontres */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-orange-400" />
          Cadence des rencontres
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Comité PRIsME</strong> : 2 fois par mois (1er et 3e mardi, 10h-11h30)
              <p className="text-sm text-gray-400 mt-1">Animé par Francis Marquis (Facilitateur)</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Dany Bourassa</strong> : Présent 1 fois par mois (1er mardi uniquement)
              <p className="text-sm text-gray-400 mt-1">Effort décroissant : 20h/sem (maintenant) → 10h en mars → 5h en juin</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Ordre du jour</strong> : Distribué 48h avant, préparé par l'Owner (Éric Hains)
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Compte-rendu</strong> : Rédigé dans les 24h, partagé avec tous les membres
            </div>
          </li>
        </ul>
      </div>

      {/* Règles de décision */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-orange-400" />
          Règles de décision
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions opérationnelles</strong> : Owner (<strong className="text-emerald-300">Éric Hains</strong>) décide, Comité consulté
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions stratégiques</strong> : PRIsME Master (Dany) décide, Comité consulté
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions techniques</strong> : Équipe technique décide, Owner (Éric) valide
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Consensus</strong> : Recherché pour les décisions impactantes, vote si nécessaire
            </div>
          </li>
        </ul>
      </div>

      {/* Canal direct vers Dany */}
      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-6 border-2 border-purple-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-orange-400" />
          Canal direct vers Dany (PRIsME Master)
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Le canal direct vers Dany est <strong className="text-white">réservé aux rôles clés</strong> : Owner (Éric Hains) et Facilitateur (Francis Marquis).
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Pour toute demande nécessitant l'attention de Dany, utiliser le format <strong className="text-white">Decision Packet</strong> structuré :
        </p>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Contexte</strong> : Situation actuelle, enjeu, impact</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Options</strong> : Alternatives considérées avec avantages/inconvénients</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Recommandation</strong> : Option privilégiée avec justification</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Décision requise</strong> : Question claire et action attendue</span>
          </li>
        </ul>
        <p className="text-gray-300 leading-relaxed mt-4 text-sm italic">
          Ce format garantit une décision rapide et éclairée, tout en respectant le temps limité de Dany.
        </p>
      </div>

      {/* Quand escalader */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-yellow-400" />
          Quand escalader
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Impact organisationnel majeur</strong> : Changements affectant tous les Primes</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Investissement significatif</strong> : Dépenses &gt; 10k$ ou &gt; 40h de développement</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Conflit de vision</strong> : Désaccord sur la direction stratégique</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Risque réputationnel</strong> : Décisions affectant l'image de BZ</span>
          </li>
        </ul>
      </div>

      {/* Quand ne PAS escalader */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
          Quand ne PAS escalader
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions opérationnelles routinières</strong> : Owner (Éric) peut décider seul
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Corrections de bugs</strong> : Équipe technique gère
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Améliorations UX mineures</strong> : Owner (Éric) valide, pas besoin de Comité
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Documentation et formation</strong> : Processus standard, pas d'escalade
            </div>
          </li>
        </ul>
      </div>

      {/* Lien avec Shield / Decision Desk */}
      <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl p-6 border-2 border-indigo-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-orange-400" />
          Lien conceptuel avec Shield / Decision Desk
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Les décisions stratégiques PRIsME sont documentées dans Shield (Decision Desk) pour traçabilité et alignement organisationnel. 
          <strong className="text-white"> Shield agit comme pare-feu mental</strong> : il structure les décisions importantes et évite la surcharge cognitive.
        </p>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span>Décisions majeures PRIsME → Shield Decision Desk (format Decision Packet)</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span>Roadmap PRIsME alignée avec roadmap Shield</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span>KPI PRIsME intégrés aux tableaux de bord Shield</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <span>Shield = pare-feu mental pour éviter la surcharge décisionnelle</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
